import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Send, Mail, Phone, MapPin, Clock, 
  CheckCircle, Loader2, Building2, DollarSign 
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { PageSEO, SEO_CONFIG, BreadcrumbSchema } from '../lib/seo';
import { GA_EVENT_NAMES, trackGaEvent } from '../lib/analytics';
import { BRAND } from '../components/layout/Layout';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    project_type: '',
    budget: '',
    message: '',
    honeypot: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const projectTypes = [
    'Digital Product Development',
    'Staff Augmentation',
    'Dedicated Team',
    'AI Strategy Consulting',
    'Business Automation',
    'Custom Development',
    'Other'
  ];

  const budgetRanges = [
    'Under $25,000',
    '$25,000 - $50,000',
    '$50,000 - $100,000',
    '$100,000 - $250,000',
    '$250,000+'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Honeypot check
    if (formData.honeypot) {
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      trackGaEvent(GA_EVENT_NAMES.CONTACT_FORM_SUBMIT);
      setSubmitted(true);
      toast.success('Thank you! We\'ll be in touch shortly.');
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="contact-success" className="min-h-[80vh] flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md mx-auto px-4"
        >
          <div className="h-20 w-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>
          <h1 className="font-heading font-bold text-3xl text-text-primary mb-4">
            Message Sent!
          </h1>
          <p className="text-text-secondary mb-8">
            Thank you for reaching out. Our team will review your inquiry and get back to you within 24 hours.
          </p>
          <Button
            onClick={() => {
              setSubmitted(false);
              setFormData({
                name: '',
                company: '',
                email: '',
                phone: '',
                project_type: '',
                budget: '',
                message: '',
                honeypot: ''
              });
            }}
            className="bg-primary text-white hover:bg-primary-hover rounded-full"
          >
            Send Another Message
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="contact-page">
      <PageSEO 
        title={SEO_CONFIG.contact.title}
        description={SEO_CONFIG.contact.description}
        keywords={SEO_CONFIG.contact.keywords}
        canonical="/contact"
      />
      <BreadcrumbSchema items={[
        { name: 'Home', url: '/' },
        { name: 'Contact', url: '/contact' }
      ]} />
      
      {/* Hero */}
      <section className="bg-gradient-to-br from-warm-50 to-white py-24" aria-labelledby="contact-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="inline-block text-primary font-medium mb-4">Contact {BRAND.name}</span>
            <h1 id="contact-heading" className="font-heading font-bold text-4xl sm:text-5xl text-text-primary mb-6">
              Get a Free IT Consultation
            </h1>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              Tell us about your <strong>software development</strong> needs and our IT experts will get back to you 
              within 24 hours with a customized solution.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-24 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="lg:col-span-1"
            >
              <h2 className="font-heading font-bold text-2xl text-text-primary mb-8">
                Contact Information
              </h2>

              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    <Mail className="h-6 w-6 text-primary" />
                  </div>
                  <div className="ml-4">
                    <p className="font-medium text-text-primary">Email</p>
                    <a
                      href="mailto:admin@plutops.com"
                      className="text-text-secondary hover:text-primary"
                      onClick={() => trackGaEvent(GA_EVENT_NAMES.EMAIL_CLICK)}
                    >
                      admin@plutops.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    <Phone className="h-6 w-6 text-primary" />
                  </div>
                  <div className="ml-4">
                    <p className="font-medium text-text-primary">Phone</p>
                    <a
                      href="tel:+91 7772968401"
                      className="text-text-secondary hover:text-primary"
                      onClick={() => trackGaEvent(GA_EVENT_NAMES.PHONE_CLICK)}
                    >
                      +91 7772968401
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    <MapPin className="h-6 w-6 text-primary" />
                  </div>
                  <div className="ml-4">
                    <p className="font-medium text-text-primary">Location</p>
                    <p className="text-text-secondary">
                      Global presence with remote teams worldwide
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    <Clock className="h-6 w-6 text-primary" />
                  </div>
                  <div className="ml-4">
                    <p className="font-medium text-text-primary">Response Time</p>
                    <p className="text-text-secondary">
                      Within 24 hours on business days
                    </p>
                  </div>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="mt-12 p-6 bg-slate-50 rounded-2xl">
                <h3 className="font-heading font-bold text-lg text-text-primary mb-4">
                  Why Work With Us
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-center text-text-secondary">
                    <CheckCircle className="h-5 w-5 text-primary mr-2" />
                    100+ successful projects
                  </li>
                  <li className="flex items-center text-text-secondary">
                    <CheckCircle className="h-5 w-5 text-primary mr-2" />
                    95% client satisfaction rate
                  </li>
                  <li className="flex items-center text-text-secondary">
                    <CheckCircle className="h-5 w-5 text-primary mr-2" />
                    Average 40% cost savings
                  </li>
                  <li className="flex items-center text-text-secondary">
                    <CheckCircle className="h-5 w-5 text-primary mr-2" />
                    Dedicated project managers
                  </li>
                </ul>
              </div>
            </motion.div>

            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="lg:col-span-2"
            >
              <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg border border-slate-100 p-8">
                {/* Honeypot field - hidden from users */}
                <input
                  type="text"
                  name="honeypot"
                  value={formData.honeypot}
                  onChange={handleChange}
                  className="hidden"
                  tabIndex={-1}
                  autoComplete="off"
                />

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-text-primary">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      data-testid="contact-name-input"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="John Doe"
                      className="h-12"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="company" className="text-text-primary">Company Name *</Label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-text-muted" />
                      <Input
                        id="company"
                        name="company"
                        data-testid="contact-company-input"
                        value={formData.company}
                        onChange={handleChange}
                        required
                        placeholder="Acme Inc."
                        className="h-12 pl-10"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-text-primary">Email Address *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-text-muted" />
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        data-testid="contact-email-input"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="john@company.com"
                        className="h-12 pl-10"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-text-primary">Phone Number</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-text-muted" />
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        data-testid="contact-phone-input"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+1 (234) 567-890"
                        className="h-12 pl-10"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-text-primary">Project Type *</Label>
                    <Select
                      value={formData.project_type}
                      onValueChange={(value) => handleSelectChange('project_type', value)}
                      required
                    >
                      <SelectTrigger data-testid="contact-project-type-select" className="h-12">
                        <SelectValue placeholder="Select project type" />
                      </SelectTrigger>
                      <SelectContent>
                        {projectTypes.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-text-primary">Budget Range</Label>
                    <Select
                      value={formData.budget}
                      onValueChange={(value) => handleSelectChange('budget', value)}
                    >
                      <SelectTrigger data-testid="contact-budget-select" className="h-12">
                        <DollarSign className="h-5 w-5 text-text-muted mr-2" />
                        <SelectValue placeholder="Select budget range" />
                      </SelectTrigger>
                      <SelectContent>
                        {budgetRanges.map((range) => (
                          <SelectItem key={range} value={range}>
                            {range}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="mt-6 space-y-2">
                  <Label htmlFor="message" className="text-text-primary">Project Details *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    data-testid="contact-message-textarea"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    placeholder="Tell us about your project, goals, and any specific requirements..."
                    className="resize-none"
                  />
                </div>

                <div className="mt-8">
                  <Button
                    type="submit"
                    data-testid="contact-submit-btn"
                    disabled={loading}
                    className="w-full md:w-auto bg-primary text-white hover:bg-primary-hover rounded-full px-8 py-6 text-lg"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message
                        <Send className="ml-2 h-5 w-5" />
                      </>
                    )}
                  </Button>
                </div>

                <p className="mt-4 text-sm text-text-muted">
                  By submitting this form, you agree to our privacy policy. We'll only use your information to respond to your inquiry.
                </p>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
